/*
  Isekaid Sword-Slash Page Transition
  Faster Original Version
  File: assets/isekaid-page-transition.js
*/

(() => {
  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  if (reducedMotion.matches) {
    return;
  }

  const STORAGE_KEY =
    'isekaid-page-transition-pending';

  /*
    Faster than the original version while still leaving
    enough time for the panels, slash, and logo to register.
  */
  const TIMING = {
    slashStart: 265,
    brandStart: 295,
    navigateAt: 590,
    openingDelay: 45,
    cleanupAfterOpening: 570
  };

  let transitionRunning = false;
  let destinationUrl = null;

  /* =======================================================
     CREATE OVERLAY
     ======================================================= */

  const overlay = document.createElement('div');

  overlay.className = 'isekaid-page-transition';
  overlay.setAttribute('aria-hidden', 'true');

  overlay.innerHTML = `
    <div
      class="
        isekaid-page-transition__panel
        isekaid-page-transition__panel--left
      "
    ></div>

    <div
      class="
        isekaid-page-transition__panel
        isekaid-page-transition__panel--right
      "
    ></div>

    <div class="isekaid-page-transition__flash"></div>

    <div class="isekaid-page-transition__brand">
      <span class="isekaid-page-transition__fallback-logo">
        Isekaid Apparel
      </span>
    </div>

    <div class="isekaid-page-transition__slash"></div>

    <div
      class="isekaid-page-transition__slash-secondary"
    ></div>
  `;

  document.body.appendChild(overlay);

  const brandContainer = overlay.querySelector(
    '.isekaid-page-transition__brand'
  );

  /* =======================================================
     FIND THE EXISTING HEADER LOGO
     ======================================================= */

  const findExistingLogo = () => {
    const selectors = [
      'header img[alt*="Isekaid" i]',
      'header .header__heading-logo',
      'header [class*="logo"] img',
      '.shopify-section-group-header-group img[alt*="Isekaid" i]',
      '.shopify-section-group-header-group [class*="logo"] img'
    ];

    for (const selector of selectors) {
      const image = document.querySelector(selector);

      if (
        image instanceof HTMLImageElement &&
        (image.currentSrc || image.src)
      ) {
        return image;
      }
    }

    return null;
  };

  const installLogo = () => {
    const existingLogo = findExistingLogo();

    if (!existingLogo || !brandContainer) {
      return;
    }

    const logo = document.createElement('img');

    logo.className =
      'isekaid-page-transition__logo';

    logo.src =
      existingLogo.currentSrc ||
      existingLogo.src;

    logo.alt = 'Isekaid Apparel';

    brandContainer.replaceChildren(logo);
  };

  installLogo();

  /* =======================================================
     LINKS THAT SHOULD NOT USE THE TRANSITION
     ======================================================= */

  const SKIP_CLOSEST_SELECTOR = [
    '[data-no-page-transition]',
    '.isekaid-no-transition',
    '.shopify-payment-button',
    '.cart-drawer',
    '.menu-drawer',
    '.predictive-search',
    '[data-shopify-editor-block]',
    '[data-shopify-editor-section]'
  ].join(',');

  const shouldTransitionLink = (
    link,
    event
  ) => {
    if (!(link instanceof HTMLAnchorElement)) {
      return false;
    }

    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return false;
    }

    if (
      link.closest(SKIP_CLOSEST_SELECTOR) ||
      link.hasAttribute('download') ||
      link.target === '_blank'
    ) {
      return false;
    }

    const rawHref = link.getAttribute('href');

    if (
      !rawHref ||
      rawHref.startsWith('#') ||
      rawHref.startsWith('mailto:') ||
      rawHref.startsWith('tel:') ||
      rawHref.startsWith('sms:') ||
      rawHref.startsWith('javascript:')
    ) {
      return false;
    }

    let url;

    try {
      url = new URL(
        link.href,
        window.location.href
      );
    } catch {
      return false;
    }

    if (
      url.origin !==
      window.location.origin
    ) {
      return false;
    }

    if (
      url.pathname ===
        window.location.pathname &&
      url.search ===
        window.location.search &&
      url.hash
    ) {
      return false;
    }

    if (
      url.pathname.startsWith('/admin') ||
      url.pathname.startsWith(
        '/services/login_with_shop'
      )
    ) {
      return false;
    }

    return true;
  };

  /* =======================================================
     EXIT TRANSITION
     ======================================================= */

  const beginExitTransition = (url) => {
    if (transitionRunning) {
      return;
    }

    transitionRunning = true;
    destinationUrl = url;

    document.documentElement.style.overflow =
      'hidden';

    overlay.classList.remove(
      'is-entering',
      'is-opening',
      'is-slashing',
      'is-brand-visible'
    );

    overlay.classList.add(
      'is-active',
      'is-closing'
    );

    window.setTimeout(() => {
      overlay.classList.add('is-slashing');
    }, TIMING.slashStart);

    window.setTimeout(() => {
      overlay.classList.add(
        'is-brand-visible'
      );
    }, TIMING.brandStart);

    window.setTimeout(() => {
      try {
        sessionStorage.setItem(
          STORAGE_KEY,
          '1'
        );
      } catch {
        /*
          Navigation still works when storage
          is unavailable.
        */
      }

      window.location.assign(destinationUrl);
    }, TIMING.navigateAt);
  };

  document.addEventListener(
    'click',
    (event) => {
      const link =
        event.target.closest?.('a[href]');

      if (
        !shouldTransitionLink(
          link,
          event
        )
      ) {
        return;
      }

      event.preventDefault();

      beginExitTransition(link.href);
    }
  );

  /* =======================================================
     DESTINATION-PAGE OPENING
     ======================================================= */

  const shouldPlayEntrance = () => {
    try {
      const pending =
        sessionStorage.getItem(
          STORAGE_KEY
        ) === '1';

      if (pending) {
        sessionStorage.removeItem(
          STORAGE_KEY
        );
      }

      return pending;
    } catch {
      return false;
    }
  };

  const playEntranceTransition = () => {
    overlay.classList.remove(
      'is-active',
      'is-closing',
      'is-slashing'
    );

    overlay.classList.add(
      'is-entering',
      'is-brand-visible'
    );

    document.documentElement.style.overflow =
      'hidden';

    /*
      Paint the closed panels first, then
      immediately begin opening them.
    */
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          overlay.classList.remove(
            'is-entering',
            'is-brand-visible'
          );

          overlay.classList.add(
            'is-active',
            'is-opening'
          );
        }, TIMING.openingDelay);
      });
    });

    window.setTimeout(() => {
      overlay.classList.remove(
        'is-active',
        'is-opening'
      );

      document.documentElement.style
        .removeProperty('overflow');
    }, TIMING.cleanupAfterOpening);
  };

  if (shouldPlayEntrance()) {
    playEntranceTransition();
  }

  /* =======================================================
     BACK/FORWARD CACHE CLEANUP
     ======================================================= */

  window.addEventListener(
    'pageshow',
    (event) => {
      if (!event.persisted) {
        return;
      }

      transitionRunning = false;
      destinationUrl = null;

      overlay.className =
        'isekaid-page-transition';

      document.documentElement.style
        .removeProperty('overflow');

      document.documentElement.classList.remove(
        'isekaid-transition-pending'
      );
    }
  );

  /* =======================================================
     ESCAPE SAFETY
     ======================================================= */

  document.addEventListener(
    'keydown',
    (event) => {
      if (
        event.key !== 'Escape' ||
        !transitionRunning
      ) {
        return;
      }

      transitionRunning = false;
      destinationUrl = null;

      overlay.className =
        'isekaid-page-transition';

      document.documentElement.style
        .removeProperty('overflow');

      document.documentElement.classList.remove(
        'isekaid-transition-pending'
      );
    }
  );
})();